import $ from 'jquery';

import LinkedOptions from '../components/LinkedOptions';
import ThemeUtils from './ThemeUtils';

export default class VariantHelper {
  constructor(options) {
    const defaultOptions = {
      $addToCartButton: null, // Set with object of add to cart button
      $priceFields: null, // Set with object containing price(s)
      $productForm: null, // Set with object containing the `data-product-form` attribute
      $productThumbnails: null, // Set with object containing thumbnails
      $selector: null, // Set with an object that contains select/input[type=checkbox] objects
      type: 'select', // Or radio
      productJSON: null, // Set with product JSON information
      productSettings: null, // Set with JSON containing product message and/or display options
      onVariantChange: () => {}, // Callback to run on variant change
    };

    this.options = ThemeUtils.extend(defaultOptions, options);

    this.$body = $(document.body);
    this.linkedOptions = null;
    this.enableHistory = false;
    this.$masterSelect = this.options.$productForm.find(`#product-select-${this.options.formID}`);

    // Disabled history state updating in the TE
    const isShopify = window.Shopify && window.Shopify.preview_host;

    if (
      window.history
      && window.history.replaceState
      && this.options.productSettings.enableHistory
      && !isShopify
    ) {
      this.enableHistory = true;
    }

    this._init();
    this._bindEvents();
  }

  getSelectedVariant() {
    return this._getVariantFromOptions(this._getCurrentOptions());
  }

  _init() {
    if (this.options.type === 'select') {
      for (let i = 0; i < this.options.$selector.length; i++) {
        const select = this.options.$selector[i];
        this._setSelectLabel(null, $(select));
      }
    }

    if (this.options.productSettings.linkedOptions) {
      this.linkedOptions = new LinkedOptions(this.options);
    }
  }

  _bindEvents() {
    return this.options.$selector.on('change', event => this._variantChange(event));
  }

  _setSelectLabel(event = null, $target) {
    let $element = $target;

    if ($target == null) {
      $element = false;
    }

    if (!$target) {
      $element = $(event.currentTarget);
    }

    const selectedOption = $element.find('option:selected').val();
    return $element
      .prev('[data-select-text]')
      .find('[data-selected-option]')
      .text(selectedOption);
  }

  _getCurrentOptions() {
    const productOptions = [];
    let $inputs = this.options.$selector;

    if (this.options.type === 'radio') {
      $inputs = $inputs.filter(':checked');
    }

    $inputs.each((index, element) => productOptions.push($(element).val()));

    return productOptions;
  }

  _getVariantFromOptions(productOptions) {
    if (this.options.productJSON.variants == null) { return; }

    let foundVariant = null;
    this.options.productJSON.variants.forEach((variant) => {
      let productOption = productOptions[0];
      for (let i = 1; i < productOptions.length; i++) {
        productOption += ` / ${productOptions[i]}`;
      }

      if (productOption === variant.title) {
        foundVariant = variant;
      }
    });

    return foundVariant;
  }

  _updateMasterSelect(variant) {
    if (variant == null) { return; }

    this.$masterSelect.find(`[data-variant-id=${variant.id}]`).prop('selected', true);
    this.$masterSelect.trigger('change');
  }

  _updatePrice(variant) {
    const { $addToCartButton } = this.options;
    const { $priceFields } = this.options;
    const { productSettings } = this.options;

    if (variant != null) {
      if (variant.available) {
        $addToCartButton
          .val(productSettings.addToCartText)
          .removeClass('disabled')
          .removeAttr('disabled');
      } else {
        $addToCartButton
          .val(productSettings.soldOutText)
          .addClass('disabled')
          .attr('disabled', 'disabled');
      }

      if (variant.compare_at_price > variant.price) {
        $priceFields
          .find('.price .money:first-child')
          .html(Shopify.formatMoney(variant.price, Theme.moneyFormat));
        $priceFields
          .find('.original')
          .html(Shopify.formatMoney(variant.compare_at_price, Theme.moneyFormat))
          .removeClass('hidden');
      } else {
        $priceFields
          .find('.price .money')
          .html(Shopify.formatMoney(variant.price, Theme.moneyFormat));
        $priceFields
          .find('.original')
          .addClass('hidden');
      }

      const unitPrice = $priceFields.find('[data-unit-price]')[0];
      if (unitPrice && variant.unit_price_measurement) {
        const totalQuantity = unitPrice.querySelector('[data-total-quantity]');
        const unitPriceAmount = unitPrice.querySelector('[data-unit-price-amount]');
        const unitPriceMeasure = unitPrice.querySelector('[data-unit-price-measure]');

        totalQuantity.innerHTML = `${variant.unit_price_measurement.quantity_value}${variant.unit_price_measurement.quantity_unit}`;

        unitPriceAmount.innerHTML = Shopify.formatMoney(variant.unit_price, Theme.moneyFormat);
        if (variant.unit_price_measurement.reference_value === 1) {
          unitPriceMeasure.innerHTML = variant.unit_price_measurement.reference_unit;
        } else {
          unitPriceMeasure.innerHTML = `${variant.unit_price_measurement.reference_value}${variant.unit_price_measurement.reference_unit}`;
        }
        unitPrice.classList.remove('hidden');
      } else if (unitPrice) {
        unitPrice.classList.add('hidden');
      }
    } else {
      // Variant doesn't exist
      $addToCartButton
        .val(productSettings.unavailableText)
        .addClass('disabled')
        .attr('disabled', 'disabled');
    }
  }

  _updateTaxLine(variant) {
    if (!variant) return;

    const taxLine = this.options.$priceFields[0].querySelector('[data-tax-line]');

    if (!taxLine) return;

    if (variant.taxable) {
      taxLine.classList.remove('product-details__tax-label--hidden');
    } else {
      taxLine.classList.add('product-details__tax-label--hidden');
    }
  }

  _updateImages(variant) {
    let imageId;
    if (variant && variant.featured_media && variant.featured_media.id) {
      imageId = variant.featured_media.id;
    } else {
      imageId = null;
    }

    if (imageId == null) { return; }

    this.options.$productThumbnails.filter(`[data-media='${imageId}']`).trigger('click');
  }

  _updateHistory(variant) {
    if (!this.enableHistory || (variant == null)) { return; }

    const newUrl = [
      window.location.protocol,
      '//',
      window.location.host,
      window.location.pathname,
      '?variant=',
      variant.id,
    ];

    const variantUrl = newUrl.join('');
    window.history.replaceState({ path: variantUrl }, '', variantUrl);
  }

  _variantChange(event) {
    if (this.options.type === 'select') {
      this._setSelectLabel(event);
    }

    const productOptions = this._getCurrentOptions();
    const variant = this._getVariantFromOptions(productOptions);

    this.options.onVariantChange(variant);
    this._updateMasterSelect(variant);
    this._updatePrice(variant);
    this._updateTaxLine(variant);
    this._updateImages(variant);
    this._updateHistory(variant);
  }

  prepareRemove() {
    this.options.$selector.off('change');

    return (this.linkedOptions != null ? this.linkedOptions.prepareRemove() : undefined);
  }
}
